import React from 'react'
import PropTypes from 'prop-types'
import {enrollmentFetch} from 'hec/builder/helpers/perksAndRewardsHelpers'
import {isPreviewing, isEditing} from '@builder.io/sdk-react'
import {Button} from '@/components/cta'
import {BodyCopy} from '@/components/ui/body-copy'
import {buttonFunctions} from 'hec/builder/blocks/cta'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import {cn} from '@/lib/utils'
import {mockCustomerData, perksAndRewardsSectionStateMap, generateSubfields} from './utils'
import {useQueryClient} from '@tanstack/react-query'
import builderConfig from 'hec/builder/model-map'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import usePerksAndRewards from 'hec/hooks/annex-cloud/usePerksAndRewards'

const CTALinkComponent = ({
    queryParams,
    ctaLink,
    ctaText,
    className,
    'data-testid': dataTestId,
    ctaMode
}) => {
    const shouldQuery = !ctaLink?.value?.data

    const {data: ctaLinkData, error: ctaLinkError} = useFetchBuilderReference({
        model: 'page',
        shouldQuery: shouldQuery,
        referenceId: ctaLink?.id
    })

    const pageReference = shouldQuery ? ctaLinkData?.data : ctaLink?.value?.data

    const clickFunction =
        !ctaMode || ctaMode === 'link' ? buttonFunctions.linkToPage : buttonFunctions.nothing

    const {routeType, url} = useDetermineRouteTypeAndUrl({
        clickFunction,
        isInternalLink: true,
        pageReference
    })

    if (shouldQuery && ctaLinkError) {
        console.error(`An error occurred while fetching article data: `, ctaLinkError)
    }

    return (
        <Button
            ctaType="link"
            clickFunction={clickFunction}
            variant="normal"
            queryParams={queryParams}
            url={url}
            routeType={routeType}
            className={cn('underline body-sm ml-2', className)}
            data-testid={dataTestId || 'perksAndRewardsSection-Link'}
        >
            {ctaText}
        </Button>
    )
}

CTALinkComponent.propTypes = {
    queryParams: PropTypes.array,
    ctaLink: PropTypes.object.isRequired,
    ctaText: PropTypes.string.isRequired,
    className: PropTypes.string,
    'data-testid': PropTypes.string,
    ctaMode: PropTypes.oneOf(['link', 'drawer'])
}

export const PerksAndRewardsSection = (props) => {
    const {attributes, brandPerksImg, perksAndRewardsData, stateToShowInEditor} = props
    const {className, ...restAttributes} = attributes
    const isPreview = isPreviewing()
    const isEdit = isEditing()
    const queryClient = useQueryClient()

    const {
        customer: customerData,
        site,
        isCustomerError,
        customerError,
        pointsEarned,
        currentTier,
        userState
    } = usePerksAndRewards({isEdit, stateToShowInEditor})

    const customer = isEdit ? mockCustomerData : customerData

    const contentData = perksAndRewardsData?.[userState]
    const {ctaLink, queryParams, ctaText} = contentData || {}

    const renderTierInfo = () => (
        <div className="flex items-center justify-end">
            <BodyCopy
                size="small"
                className="!font-medium inline"
                data-testid="perksAndRewardsSection-BodyCopy"
            >
                {currentTier} Tier |
            </BodyCopy>
            <CTALinkComponent
                queryParams={queryParams}
                ctaLink={ctaLink}
                ctaText={ctaText}
                className="mt-1"
                data-testid="perksAndRewardsSection-CTALinkComponent"
            />
        </div>
    )

    const renderPerksDetails = () => {
        let content = <></>

        if (userState === perksAndRewardsSectionStateMap.LOGGED_OUT) {
            const {prompt} = contentData || {}
            const {ctaMode} = perksAndRewardsData?.[userState] || {}
            const isDrawerMode = ctaMode === 'drawer'
            return (
                <div className="text-blue-04 w-3/5 text-right pr-2">
                    <BodyCopy
                        size="small"
                        className="inline"
                        data-testid="perksAndRewardsSection-BodyCopy"
                    >
                        {prompt}
                    </BodyCopy>
                    {isDrawerMode ? (
                        <a
                            href="#login"
                            className="inline"
                            data-testid="perksAndRewardsSection-LoginLink"
                        >
                            <CTALinkComponent
                                queryParams={queryParams}
                                ctaLink={ctaLink}
                                ctaText={ctaText}
                                data-testid="perksAndRewardsSection-CTALinkComponent"
                                ctaMode={ctaMode}
                            />
                        </a>
                    ) : (
                        <CTALinkComponent
                            queryParams={queryParams}
                            ctaLink={ctaLink}
                            ctaText={ctaText}
                            data-testid="perksAndRewardsSection-CTALinkComponent"
                            ctaMode="link"
                        />
                    )}
                </div>
            )
        } else if (userState === perksAndRewardsSectionStateMap.NOT_ENROLLED) {
            const {prompt} = contentData || {}
            content = (
                <>
                    <BodyCopy
                        size="small"
                        className="inline"
                        data-testid="perksAndRewardsSection-BodyCopy"
                    >
                        {prompt}
                    </BodyCopy>
                    <Button
                        ctaType="button"
                        variant="normal"
                        onClick={() =>
                            enrollmentFetch(site.id, () =>
                                queryClient.invalidateQueries({
                                    queryKey: ['checkEnrollment', customer?.email, site.id],
                                    exact: true
                                })
                            )
                        }
                        className="underline body-sm ml-2 h-auto"
                        data-testid="perksAndRewardsSection-Button"
                    >
                        {ctaText}
                    </Button>
                </>
            )
        } else if (userState === perksAndRewardsSectionStateMap.ENROLLED_WITH_NO_POINTS) {
            const {prompt} = contentData
            content = (
                <>
                    <BodyCopy
                        size="small"
                        className="mb-1 lg:mb-0"
                        data-testid="perksAndRewardsSection-BodyCopy"
                    >
                        {prompt}
                    </BodyCopy>
                    {renderTierInfo()}
                </>
            )
        } else if (userState === perksAndRewardsSectionStateMap.ENROLLED_WITH_POINTS) {
            content = (
                <>
                    <BodyCopy
                        size="small"
                        className="!font-medium inline"
                        data-testid="perksAndRewardsSection-BodyCopy"
                    >
                        {pointsEarned}
                    </BodyCopy>
                    <BodyCopy
                        size="small"
                        className="inline"
                        data-testid="perksAndRewardsSection-BodyCopy"
                    >
                        {' '}
                        Perks Points
                    </BodyCopy>

                    {renderTierInfo()}
                </>
            )
        }
        return <div className="text-blue-04 w-3/5 text-right pr-2">{content}</div>
    }

    if (isCustomerError) {
        if (isPreview) {
            throw new Error(customerError)
        } else {
            console.error(customerError)
        }
    }
    return (
        <div
            className={cn(
                'flex flex-row space-between gap-2 p-2 items-center justify-center border border-solid border-x-0',
                className
            )}
            {...restAttributes}
        >
            {brandPerksImg && (
                <img src={brandPerksImg} className="w-2/5 contain" alt="Brand Perks Logo" />
            )}
            {renderPerksDetails()}
        </div>
    )
}
PerksAndRewardsSection.propTypes = {
    attributes: PropTypes.shape({
        className: PropTypes.string
    }),
    brandPerksImg: PropTypes.string.isRequired,
    perksAndRewardsData: PropTypes.shape({
        loggedOut: PropTypes.shape({
            prompt: PropTypes.string,
            ctaLink: PropTypes.object.isRequired,
            ctaText: PropTypes.string.isRequired
        }).isRequired,
        notEnrolled: PropTypes.shape({
            prompt: PropTypes.string,
            ctaLink: PropTypes.object.isRequired,
            ctaText: PropTypes.string.isRequired
        }).isRequired,
        enrolledWithNoPoints: PropTypes.shape({
            prompt: PropTypes.string,
            ctaLink: PropTypes.object.isRequired,
            ctaText: PropTypes.string.isRequired
        }).isRequired,
        enrolledWithPoints: PropTypes.shape({
            ctaLink: PropTypes.object.isRequired,
            ctaText: PropTypes.string.isRequired
        })
    }).isRequired,
    stateToShowInEditor: PropTypes.oneOf(Object.values(perksAndRewardsSectionStateMap)).isRequired
}

export const PerksAndRewardsSectionDefinition = {
    component: PerksAndRewardsSection,
    name: 'PerksAndRewardsSection',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/shape-square.svg',
    noWrap: true,
    inputs: [
        {
            name: 'perksAndRewardsData',
            type: 'object',
            required: true,
            subFields: generateSubfields()
        },
        {
            name: 'brandPerksImg',
            type: 'file',
            required: true
        },
        {
            name: 'stateToShowInEditor',
            type: 'string',
            enum: Object.values(perksAndRewardsSectionStateMap),
            defaultValue: perksAndRewardsSectionStateMap.LOGGED_OUT
        },
        {
            name: 'accountFormLabels',
            type: 'reference',
            model: builderConfig.accountFormLabels,
            helperText:
                'An account form drawer is triggered in this componenet if the user is not signed in. Select account form labels to override the default set on the page.'
        }
    ]
}
